import React, { useState } from "react";
import { FaCubes, FaFilePdf, FaChevronDown, FaChevronUp } from "react-icons/fa";

const PatientCardList = ({appMode, patients, onViewPdf, onViewModel }) => {

  const MODEL_URL = window.API_BASE_URL + "model";
  const PDF_URL = window.API_BASE_URL + "pdf";
  const [lastViewedPatientId, setLastViewedPatientId] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCardExpansion = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getDateFromTicks = (dotnetTicks) => {
    if (!dotnetTicks || isNaN(dotnetTicks)) return "-";

    const DOTNET_JAVASCRIPT_EPOCH_DIFFERENCE = 621355968000000000;
    const ticksPerMillisecond = 10000;
    const jsMilliseconds = (dotnetTicks - DOTNET_JAVASCRIPT_EPOCH_DIFFERENCE) / ticksPerMillisecond;

    const date = new Date(jsMilliseconds);

    // Return a formatted string, not a Date object
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
      //hour: "2-digit",
      //minute: "2-digit",
    });
  };  

  if (!patients.length)
    return <div className="text-center text-gray-500 mt-6">No patients found.</div>;

  return (
    <div className="flex flex-col gap-2">
      {patients.map((p, i) => (
        <div key={i} className="bg-white shadow-sm rounded-lg border border-gray-200 hover:border-blue-300 transition-all">
          {/* Main Card Header - Always Visible */}
          <div className="flex items-center justify-between p-3">
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-sm text-gray-800 leading-tight">
                    {appMode === "production" ? (
                      <>
                        {p.first_name || p.last_name ? (
                          `${p.first_name} ${p.last_name}`
                        ) : (
                          p.name
                        )}                
                      </>
                    ) : (
                      <>
                        {p.name}
                      </>
                    )} 
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    ID: {appMode === "production" ? (p.pid || "-") : (p.patient_id || "-")}
                  </p>
                </div>
                
                {/* Action Buttons - Always Visible */}
                <div className="flex gap-2 ml-3">
                  {p.model_drive_id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLastViewedPatientId(`${p.patient_id}-${p.hospital_id}`);
                        localStorage.setItem("lastViewedPatientId", `${p.patient_id}-${p.hospital_id}`);
                        onViewModel({
                          patientId: p.patient_id,
                          patientName:
                            p.first_name || p.last_name
                            ? `${p.first_name} ${p.last_name}`
                            : p.name,
                          modelUrl: `${MODEL_URL}/${p.model_drive_id}`,
                          studyDate: getDateFromTicks(p.dor),
                        });
                      }}
                      className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white 
                                 rounded hover:bg-blue-600 transition-colors"
                      title="View 3D Model">
                      <FaCubes className="text-xs" />
                    </button>
                  ) : (
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-400 rounded">
                      <FaCubes className="text-xs" />
                    </div>
                  )}
                
                  {p.pdf_drive_id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLastViewedPatientId(`${p.patient_id}-${p.hospital_id}`);
                        localStorage.setItem("lastViewedPatientId", `${p.patient_id}-${p.hospital_id}`);
                        onViewPdf({
                          patientId: p.patient_id,
                          fileId: p.pdf_drive_id,
                          pdfUrl: `${PDF_URL}/${p.pdf_drive_id}`,
                        });
                      }}
                      className="flex items-center justify-center w-8 h-8 bg-red-500 text-white 
                                 rounded hover:bg-red-600 transition-colors"
                      title="View PDF Report">
                      <FaFilePdf className="text-xs" />
                    </button>
                  ) : (
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-400 rounded">
                      <FaFilePdf className="text-xs" />
                    </div>
                  )}
                  
                  {/* Dropdown Toggle */}
                  <button
                    onClick={() => toggleCardExpansion(i)}
                    className="flex items-center justify-center w-8 h-8 bg-gray-200 text-gray-600 
                               rounded hover:bg-gray-300 transition-colors"
                    title="View Details">
                    {expandedCards[i] ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Expandable Details - Hidden by Default */}
          {expandedCards[i] && (
            <div className="px-3 pb-3 border-t border-gray-100">
              {appMode === "production" ? (
                <div className="pt-2 space-y-1">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-700">DOB:</span> {getDateFromTicks(p.dob) || "-"}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-700">Registered:</span> {getDateFromTicks(p.dor) || "-"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="pt-2 space-y-2">
                  {/* Patient History */}
                  {p.history?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-800 mb-1">Patient History</h4>
                      <ul className="list-disc list-inside text-xs text-gray-700 space-y-0.5 ml-2">
                        {p.history.map((h, i) => (
                          <li key={i} className="leading-snug">
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
      
                  {/* Surgery Details */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-800 mb-1">Surgery Details</h4>
                    <div className="grid grid-cols-1 gap-1">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">OT:</span> {p.surgery_venue || "-"}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">Surgery:</span> {p.surgery_type || "-"}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">Surgeon:</span> {p.surgeon || "-"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PatientCardList;
